import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { EventService } from "../../services/event_service";
import config from "../../config/config";
import styles from "./Event.module.css";
import Subscription from "../Subscription/Subscription";
import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCalendar,
  FaFire,
  FaArrowRight
} from "react-icons/fa";
import { useEventState } from "../../states/useEventStates";
import event from "./event.jpg"
const Event = () => {
  const eventService = EventService();
  const {
    eventData,
    setEventData,
    loading,
    setLoading,
    filter,
    setFilter
  } = useEventState();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        await eventService.fetchAllEvents(setEventData);
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEventData();
  }, []);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "/default.png";
    if (imagePath.startsWith("http")) return imagePath;
    return `${config.baseURL}${imagePath}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // IMPROVED: Check if event is today
  const isEventToday = (event) => {
    if (!event.start_datetime) return false;

    const today = new Date();
    const eventDate = new Date(event.start_datetime);

    // Compare year, month, and day only (ignore time)
    return (
      today.getFullYear() === eventDate.getFullYear() &&
      today.getMonth() === eventDate.getMonth() &&
      today.getDate() === eventDate.getDate()
    );
  };

  // IMPROVED getEventStatus function
  const getEventStatus = (event) => {
    const now = new Date();

    // 1. Check if event is today
    if (isEventToday(event)) {
      const start = new Date(event.start_datetime);
      const end = event.end_datetime ? new Date(event.end_datetime) : null;

      // If event hasn't started yet today
      if (now < start) {
        return "upcoming";
      }
      // If event is ongoing today (started but not ended)
      if (!end || now <= end) {
        return "ongoing";
      }
      // If event ended today
      return "completed";
    }

    // 2. For non-today events
    if (!event.start_datetime) {
      return "upcoming";
    }

    const start = new Date(event.start_datetime);
    const end = event.end_datetime ? new Date(event.end_datetime) : null;

    if (now < start) return "upcoming";
    if (end && now >= start && now <= end) return "ongoing";
    return "completed";
  };


  const eventCategories = [
    { key: "all", label: "All Events", color: "#607d8b" },
    { key: "upcoming", label: "Upcoming", color: "#FFD700" }, 
    { key: "ongoing", label: "Ongoing", color: "#546e7a" },   
    { key: "completed", label: "Completed", color: "#00c853" } 
  ];

  const getEventCategoryColor = (key) => {
    const found = eventCategories.find(c => c.key === key);
    return found ? found.color : "#607d8b";
  };

  // Function to get events for each filter
  const getFilteredEvents = () => {
    if (filter === "all") {
      return [...eventData].sort((a, b) => {
        // Sort by: ongoing first, then upcoming, then completed
        const statusOrder = { ongoing: 1, upcoming: 2, completed: 3 };
        const statusA = getEventStatus(a);
        const statusB = getEventStatus(b);

        if (statusOrder[statusA] !== statusOrder[statusB]) {
          return statusOrder[statusA] - statusOrder[statusB];
        }

        // Then sort by date
        const dateA = a.start_datetime ? new Date(a.start_datetime) : new Date(0);
        const dateB = b.start_datetime ? new Date(b.start_datetime) : new Date(0);

        if (statusA === "upcoming") {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      });
    }

    if (filter === "ongoing") {
      return eventData.filter(event => {
        const status = getEventStatus(event);

        return status === "ongoing" || isEventToday(event);
      }).sort((a, b) => {

        const statusA = getEventStatus(a);
        const statusB = getEventStatus(b);

        if (statusA === "ongoing" && statusB !== "ongoing") return -1;
        if (statusA !== "ongoing" && statusB === "ongoing") return 1;

        const timeA = a.start_datetime ? new Date(a.start_datetime) : new Date(0);
        const timeB = b.start_datetime ? new Date(b.start_datetime) : new Date(0);
        return timeA - timeB;
      });
    }

    // For upcoming and completed filters
    return eventData.filter(event => getEventStatus(event) === filter)
      .sort((a, b) => {
        const dateA = a.start_datetime ? new Date(a.start_datetime) : new Date(0);
        const dateB = b.start_datetime ? new Date(b.start_datetime) : new Date(0);

        if (filter === "upcoming") {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      });
  };

  const filteredEvents = getFilteredEvents();

  // Count events by status
  const countEventsByStatus = () => {
    const counts = { all: eventData.length, upcoming: 0, ongoing: 0, completed: 0 };

    eventData.forEach(event => {
      const status = getEventStatus(event);
      if (status === "upcoming") counts.upcoming++;
      if (status === "ongoing") counts.ongoing++;
      if (status === "completed") counts.completed++;
    });

    return counts;
  };

  const eventCounts = countEventsByStatus();

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Events...</p>
      </div>
    );
  }

  return (
    <div className={styles.eventPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBg}><img src={event} alt="Events" /></div>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <div className={styles.container}>
            <h1 className={`${styles.heroTitle} ${styles.heroTitleAnim}`}>
              <span>Upcoming Events</span>
            </h1>
            <p className={`${styles.heroSubtitle} ${styles.heroSubtitleAnim}`}>
              Experience the thrill of Royal Premier League through our
              exclusive events
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className={styles.filterSection}>
        <div className={styles.container}>
          <div className={styles.filterTabs}>
            {eventCategories.map((category) => (
              <button
                key={category.key}
                className={`${styles.filterTab} ${filter === category.key ? styles.active : ""
                  }`}
                onClick={() => setFilter(category.key)}
                style={{
                  "--category-color": category.color,
                }}
              >
                {category.label}
                {category.key !== "all" && (
                  <> ({eventCounts[category.key]})</>
                )}
                {category.key === "all" && ` (${eventCounts.all})`}
              </button>
            ))}
          </div>
        </div>
      </section>


      {/* Events Grid */}
      <section className={`${styles.eventsSection} ${styles.section}`}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {filter === "all" ? "All Events" :
                filter === "upcoming" ? "Upcoming Events" :
                  filter === "ongoing" ? "Live & Today's Events" :
                    "Completed Events"}
            </h2>

          </div>

          {filteredEvents.length === 0 ? (
            <div className={styles.noEvents}>
              <div className={styles.noEventsIcon}>
                <FaCalendar />
              </div>
              <h3>No Events Found</h3>
              <p>
                There are no {filter !== "all" ? filter : ""} events at the
                moment.
              </p>
            </div>
          ) : (
            <div className={styles.eventsGrid}>
              {filteredEvents.map((event, index) => {
                const status = getEventStatus(event);
                const isToday = isEventToday(event);

                return (
                  <div
                    key={event.id}
                    className={`${styles.eventCard} ${styles[status]} ${isToday ? styles.todayEvent : ""}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className={styles.eventImage}>
                      <img
                        src={getFullImageUrl(event.image)}
                        alt={event.title}
                        className={styles.image}
                      />
                      <div className={styles.eventStatus}>
                        <span
                          className={`${styles.statusBadge} ${styles[status]}`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                          {isToday && " (Today)"}
                        </span>

                      </div>
                      <div className={styles.eventOverlay}></div>
                    </div>

                    <div className={styles.eventContent}>
                      <div>
                        <h3 className={styles.eventTitle}>
                          {event.title?.replace(/<[^>]*>/g, "").substring(0, 40)}
                          {event.title?.replace(/<[^>]*>/g, "").length > 40 ? "..." : ""}
                        </h3>
                        {event.description && (

                          <p
                            className={styles.eventDescription} style={{ textAlign: "justify" }}
                            dangerouslySetInnerHTML={{
                              __html: event.description
                                ? event.description.replace(/<[^>]*>/g, "").substring(0, 120)
                                : "",
                            }}
                          ></p>

                        )}

                        <div className={styles.eventDetails}>
                          {event.start_datetime ? (
                            <div className={styles.detailItem}>
                              <span className={styles.detailIcon}>
                                <FaCalendarAlt className={styles.icons} />
                              </span>
                              <div className={styles.detailText}>
                                <div className={styles.detailLabel}>Date</div>
                                <div className={styles.detailValue}>
                                  {formatDate(event.start_datetime)} {formatTime(event.start_datetime)} - {formatDate(event.end_datetime)} {formatTime(event.end_datetime)}
                                  {isToday && <span className={styles.todayIndicator}> (Today)</span>}
                                </div>
                              </div>
                            </div>
                          ) : (<div className={styles.detailItem}>
                            <span className={styles.detailIcon}>
                              <FaCalendarAlt className={styles.icons} />
                            </span>
                            <div className={styles.detailText}>
                              <div className={styles.detailLabel}>Date</div>
                              <div className={styles.detailValue}>
                                <span>Event Upcoming</span>
                              </div>
                            </div>
                          </div>)}

                          {event.location && (
                            <div className={styles.detailItem}>
                              <span className={styles.detailIcon}>
                                <FaMapMarkerAlt className={styles.icons} />
                              </span>
                              <div className={styles.detailText}>
                                <div className={styles.detailLabel}>Location</div>
                                <div className={styles.detailValue}>
                                  {event.location}
                                </div>
                              </div>
                            </div>
                          )}


                          {event.contact_no && (
                            <div className={styles.detailItem}>
                              <span className={styles.detailIcon}>
                                <FaPhoneAlt className={styles.icons} />
                              </span>
                              <div className={styles.detailText}>
                                <div className={styles.detailLabel}>Contact Number</div>
                                <div className={styles.detailValue}>
                                  {event.contact_no}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.eventActions}>
                        <Link
                          to={`/event/${event.id}`}
                          className={`${styles.actionButton} ${styles.secondary}`}
                        >
                          View Details <FaArrowRight />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <Subscription />
    </div>
  );
};

export default Event;